// src/components/superuser/CompanyDetails.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminFormData, setAdminFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [adminFormErrors, setAdminFormErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch company details
      const companyRes = await axios.get(`/companies/${id}`);
      setCompany(companyRes.data.company);

      // Fetch company admins
      const adminsRes = await axios.get(`/companies/${id}/admins`);
      setAdmins(adminsRes.data.admins);

      setError("");
    } catch (error) {
      setError(
        error.response?.data?.message || "Failed to load company details"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/superuser/dashboard");
  };

  const handleEditCompany = () => {
    navigate(`/superuser/company/${id}/edit`);
  };

  const handleAddAdminClick = () => {
    setDialogOpen(true);
    setAdminFormData({
      name: "",
      email: "",
      password: "",
      confirmPassword: "",
    });
    setAdminFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdminFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when field is changed
    if (adminFormErrors[name]) {
      setAdminFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!adminFormData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!adminFormData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(adminFormData.email)) {
      errors.email = "Email is invalid";
    }

    if (!adminFormData.password) {
      errors.password = "Password is required";
    } else if (adminFormData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (adminFormData.password !== adminFormData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setAdminFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddAdmin = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await axios.post(`/companies/${id}/admins`, {
        name: adminFormData.name,
        email: adminFormData.email,
        password: adminFormData.password,
      });

      // Refresh admins list
      const adminsRes = await axios.get(`/companies/${id}/admins`);
      setAdmins(adminsRes.data.admins);

      setDialogOpen(false);
    } catch (error) {
      setAdminFormErrors({
        submit: error.response?.data?.message || "Failed to create admin",
      });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            sx={{ mr: 2 }}
            onClick={handleBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Company Details
          </Typography>
          <Button
            color="inherit"
            startIcon={<EditIcon />}
            onClick={handleEditCompany}
          >
            Edit
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {company && (
          <>
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                {company.name}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {company.email || "Not provided"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {company.phone || "Not provided"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {company.address || "Not provided"}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">Company Administrators</Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdminClick}
                >
                  Add Admin
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {admins.length === 0 ? (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  No administrators found.
                </Typography>
              ) : (
                <List>
                  {admins.map((admin) => (
                    <ListItem key={admin._id} divider>
                      <ListItemText
                        primary={admin.name}
                        secondary={admin.email}
                        primaryTypographyProps={{ fontWeight: "medium" }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </>
        )}
      </Container>

      {/* Add Admin Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Company Administrator</DialogTitle>
        <DialogContent>
          {adminFormErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {adminFormErrors.submit}
            </Alert>
          )}
          <TextField
            name="name"
            label="Full Name"
            fullWidth
            margin="normal"
            value={adminFormData.name}
            onChange={handleInputChange}
            error={!!adminFormErrors.name}
            helperText={adminFormErrors.name}
          />
          <TextField
            name="email"
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={adminFormData.email}
            onChange={handleInputChange}
            error={!!adminFormErrors.email}
            helperText={adminFormErrors.email}
          />
          <TextField
            name="password"
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            value={adminFormData.password}
            onChange={handleInputChange}
            error={!!adminFormErrors.password}
            helperText={adminFormErrors.password}
          />
          <TextField
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            fullWidth
            margin="normal"
            value={adminFormData.confirmPassword}
            onChange={handleInputChange}
            error={!!adminFormErrors.confirmPassword}
            helperText={adminFormErrors.confirmPassword}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleAddAdmin}>
            Add Admin
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyDetails;
